import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    /*
     * These properties must be set before the video sources are loaded.
     * This is particularly important for Safari and iOS autoplay.
     */
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.autoplay = true;
    videoElement.loop = true;
    videoElement.playsInline = true;
    videoElement.controls = false;

    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('loop', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');

    videoElement.removeAttribute('controls');

    /*
     * Load the video sources only after muted and autoplay have been set.
     */
    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
      }
    }

    const playVideo = async () => {
      try {
        await videoElement.play();
        this.classList.remove('video-autoplay-blocked');
      } catch (error) {
        /*
         * Safari may block autoplay, particularly in Low Power Mode.
         * Playback will be retried after the first interaction.
         */
        this.classList.add('video-autoplay-blocked');
      }
    };

    videoElement.load();

    /*
     * Try at multiple points because Safari differs between devices.
     */
    playVideo();

    videoElement.addEventListener('loadedmetadata', playVideo, {
      once: true,
    });

    videoElement.addEventListener('canplay', playVideo, {
      once: true,
    });

    /*
     * Restart when returning to the page or browser tab.
     */
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && videoElement.paused) {
        playVideo();
      }
    });

    /*
     * Final fallback when the browser requires user interaction.
     */
    const retryAfterInteraction = () => {
      if (videoElement.paused) {
        playVideo();
      }
    };

    document.addEventListener('touchstart', retryAfterInteraction, {
      once: true,
      passive: true,
    });

    document.addEventListener('click', retryAfterInteraction, {
      once: true,
    });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define(
    'video-background-component',
    VideoBackgroundComponent
  );
}